import { useEffect, useRef, useState } from "react";
import { useFormikContext } from "formik";
import { useSnackbar } from "notistack";

import { useIoCContext } from "@context/IoCContext/IoCContext";
import { useUserState } from "@context/UserContext";
import { useHomeUserContext } from "@context/HomeUserContext";
import { useAppointmentPriceContext } from "../context/AppointmentPriceContext";

import { Types } from "@ioc/types";
import { IGetClientInfoService } from "@modules/orders/models/IGetClientInfoService";
import { WithdrawBasis } from "@utils/interfaces";

import { IQuery } from "../interface";
import { fetchCustomerData } from "../services/fetchCustomerData";
import { getDefaultAddress } from "../rules/customer.rules";
import {
  resolveWithdrawBasis,
  findWithdrawBasisByValue,
  toWithdrawBasisID,
} from "../rules/withdrawBasis.rules";

const DEPENDENT_FIELDS: Array<[string, string | null]> = [
  ["filialID", ""],
  ["withdrawBasisID", ""],
  ["withdrawBasisBranchName", ""],
  ["address", null],
  ["freightType", ""],
];

const TOUCHED_FIELDS = ["address", "withdrawBasisID", "freightType"];

export function useFormQuery() {
  const userState = useUserState();
  const ctx = useAppointmentPriceContext();
  const { enqueueSnackbar } = useSnackbar();
  const iocContext = useIoCContext();
  const { CNPJDetailsLoad } = useHomeUserContext();
  const {
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<IQuery>();

  const [progress, setProgress] = useState(10);
  const [branchesWithdrawBasis, setBranchesWithdrawBasis] = useState<
    WithdrawBasis[]
  >([]);
  const fetchIdRef = useRef(0);

  const getClientInfoService = iocContext.serviceContainer.get<
    IGetClientInfoService
  >(Types.Orders.IGetClientInfoService);

  const applyWithdrawBasis = (
    list: WithdrawBasis[],
    filialIdDefault?: string
  ) => {
    const result = resolveWithdrawBasis(list, filialIdDefault);

    if (!result) {
      setBranchesWithdrawBasis([]);
      setFieldValue("withdrawBasisBranchName", "");
      setFieldValue("withdrawBasisID", "");
      setFieldValue("filialID", "");
      return;
    }

    setFieldValue("withdrawBasisBranchName", result.selected.branchName ?? "");
    setFieldValue("withdrawBasisID", result.withdrawBasisID);
    setFieldValue("filialID", result.selected.filialID ?? "");
    setBranchesWithdrawBasis(result.branches);
  };

  const resetDependentFields = () => {
    ctx.setDataCustomer(null);
    ctx.setCustomerStatus("idle");
    setBranchesWithdrawBasis([]);
    DEPENDENT_FIELDS.forEach(([field, value]) => setFieldValue(field, value));
    TOUCHED_FIELDS.forEach((field) => setFieldTouched(field, false, false));
  };

  const handleCNPJChange = async (cnpj: string) => {
    setFieldValue("CNPJ", cnpj);
    resetDependentFields();

    if (!cnpj) return;

    const currentFetchId = ++fetchIdRef.current;
    ctx.setCustomerStatus("loading");

    const { data, error } = await fetchCustomerData(getClientInfoService, cnpj);

    if (currentFetchId !== fetchIdRef.current) return;

    if (error) {
      enqueueSnackbar(error.message, { variant: error.variant });
      ctx.setCustomerStatus("error");
      return;
    }

    if (data) {
      ctx.setDataCustomer(data);
      setFieldValue("freightType", data.freightType);

      const defaultAddress = getDefaultAddress(data);
      setFieldValue("address", defaultAddress);

      if (defaultAddress?.filialIdDefault) {
        applyWithdrawBasis(data.withdrawBasis, defaultAddress.filialIdDefault);
      }
    }

    ctx.setCustomerStatus("loaded");
  };

  const handleAddressChange = (addressId: string) => {
    const address = ctx.dataCustomer?.address.find((i) => i.id === addressId);
    setFieldValue("address", address ?? null);

    if (ctx.dataCustomer?.withdrawBasis) {
      applyWithdrawBasis(
        ctx.dataCustomer.withdrawBasis,
        address?.filialIdDefault
      );
    }
  };

  const handleWithdrawBasisChange = (value: string) => {
    const selected = findWithdrawBasisByValue(branchesWithdrawBasis, value);

    if (selected) {
      setFieldValue("withdrawBasisID", toWithdrawBasisID(selected));
      setFieldValue("filialID", selected.filialID);
    }
  };

  useEffect(() => {
    if (!CNPJDetailsLoad) return;
    const timer = setInterval(() => {
      setProgress((prev) => (prev >= 100 ? 0 : prev + 10));
    }, 800);
    return () => clearInterval(timer);
  }, [CNPJDetailsLoad]);

  const fieldErrorProps = (name: keyof IQuery) => ({
    onBlur: () => setFieldTouched(name as string, true),
    error: !!errors[name] && !!touched[name],
    helperText: touched[name] && errors[name],
  });

  return {
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    fieldErrorProps,
    cnpjOptions: userState?.listCNPJ,
    dataCustomer: ctx.dataCustomer,
    branchesWithdrawBasis,
    isLoading: ctx.customerStatus === "loading",
    CNPJDetailsLoad,
    progress,
    handleCNPJChange,
    handleAddressChange,
    handleWithdrawBasisChange,
  };
}
